import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6ebdrghk82orf0cki0";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");
  const MAX_LEN = 500;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      setMessage("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    if (String(formData.get("message") || "").length > MAX_LEN) {
      setStatus("error");
      setFormError(`留言内容不能超过 ${MAX_LEN} 个字符，请精简后再提交。`);
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        setMessage("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过验证，请稍后重试。"
            : String(serverMsg) || "提交失败，请稍后重试或通过邮箱联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试或通过邮箱联系我们。");
    }
  };

  const inputClass =
    "w-full h-11 px-4 rounded-md bg-background-900 border border-background-700 text-sm text-foreground-100 placeholder:text-foreground-500 font-label outline-none focus:border-accent-600 transition-colors";

  return (
    <form
      id="about-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-background-900/60 border border-background-800 rounded-lg p-5 md:p-7"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-300 font-label mb-2">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-300 font-label mb-2">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收我们的回复"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="contact-subject" className="block text-sm text-foreground-300 font-label mb-2">
          留言主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          placeholder="例如：资源建议 / 内容纠错 / 合作咨询"
          className={inputClass}
        />
      </div>

      <div className="mb-2">
        <label htmlFor="contact-message" className="block text-sm text-foreground-300 font-label mb-2">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={MAX_LEN}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
          placeholder="欢迎告诉我们你想要的影片、发现的错误或任何建议"
          className="w-full px-4 py-3 rounded-md bg-background-900 border border-background-700 text-sm text-foreground-100 placeholder:text-foreground-500 font-label outline-none focus:border-accent-600 transition-colors resize-none leading-relaxed"
        />
        <div className="text-right text-xs text-foreground-500 font-label mt-1">
          {message.length} / {MAX_LEN}
        </div>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot-field"
      />

      {status === "error" && formError && (
        <div className="mb-4 px-4 py-3 rounded-md bg-primary-950 border border-primary-800 text-primary-100 text-sm font-label">
          {formError}
        </div>
      )}
      {status === "success" && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-950 border border-accent-800 text-accent-200 text-sm font-label">
          留言已提交成功，感谢你的反馈，我们会尽快处理。
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交留言
          </>
        )}
      </button>
    </form>
  );
}